import { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import {
  Server,
  Mail,
  Edit2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Save,
  Users,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export const ServiceContactsAdmin = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const fetchServices = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/services/internal`);
      if (res.ok) {
        const data = await res.json();
        setServices(data);
      }
    } catch {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const openEdit = (service) => {
    setEditingService({
      ...service,
      contact_emails: (service.contact_emails || []).join(', '),
    });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editingService) return;
    setIsSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/services/internal/${editingService.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contact_name: editingService.contact_name || '',
          contact_emails: editingService.contact_emails
            ? editingService.contact_emails.split(',').map(e => e.trim()).filter(Boolean)
            : [],
          down_message: editingService.down_message || '',
        }),
      });
      if (res.ok) {
        toast.success(`Contact settings updated for ${editingService.name}`);
        setEditDialogOpen(false);
        fetchServices();
      } else {
        toast.error('Failed to update service');
      }
    } catch {
      toast.error('Failed to update service');
    } finally {
      setIsSaving(false);
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'up') return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
    if (status === 'down') return <XCircle className="w-4 h-4 text-red-500" />;
    return <AlertTriangle className="w-4 h-4 text-amber-500" />;
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="service-contacts-admin">
      <div>
        <h2 className="text-lg font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Service Contact Lists ({services.length})
        </h2>
        <p className="text-sm text-muted-foreground">
          Configure notification contacts and messages for each internal service
        </p>
      </div>

      {services.length === 0 ? (
        <Card className="bg-card/80 backdrop-blur-sm border-border/50 p-8 text-center">
          <Server className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
          <p className="text-muted-foreground">No internal services configured</p>
          <p className="text-sm text-muted-foreground mt-1">Add services from the Internal Services section on the dashboard</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {services.map((service) => (
            <Card
              key={service.id}
              className={cn(
                "bg-card/80 backdrop-blur-sm border-border/50 p-5",
                service.status === 'down' && "border-red-500/30"
              )}
              data-testid={`service-contact-${service.id}`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  {/* Service Name + Status */}
                  <div className="flex items-center gap-2 mb-3">
                    {getStatusIcon(service.status)}
                    <h3 className="font-semibold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      {service.name}
                    </h3>
                    <Badge variant="outline" className="text-xs ml-auto">
                      {service.host}{service.check_type !== 'ping' && service.port ? `:${service.port}` : ''}
                    </Badge>
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <Users className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm">
                          <span className="text-muted-foreground">Contact Group: </span>
                          <span className="font-medium">{service.contact_name || 'Not configured'}</span>
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">Notification Emails:</p>
                        {service.contact_emails && service.contact_emails.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {service.contact_emails.map((email, i) => (
                              <Badge key={i} variant="outline" className="text-xs gap-1">
                                <Mail className="w-3 h-3" />
                                {email}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-amber-500">No emails configured</p>
                        )}
                      </div>
                    </div>

                    {service.down_message && (
                      <div className="text-xs text-muted-foreground mt-1 p-2 rounded bg-muted/30">
                        <span className="font-medium">Down message: </span>
                        {service.down_message}
                      </div>
                    )}
                  </div>
                </div>

                {/* Edit Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEdit(service)}
                  className="gap-2 shrink-0"
                  data-testid={`edit-contact-${service.id}`}
                >
                  <Edit2 className="w-4 h-4" />
                  Edit Contacts
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Contact Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              Edit Contacts: {editingService?.name}
            </DialogTitle>
            <DialogDescription>
              Configure who to notify and what message to display when this service is down
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="admin-contact-name">Contact Group Name</Label>
              <Input
                id="admin-contact-name"
                placeholder="e.g., Area Manager and DOR group"
                value={editingService?.contact_name || ''}
                onChange={(e) => setEditingService({ ...editingService, contact_name: e.target.value })}
                className="mt-1.5"
                data-testid="admin-contact-name-input"
              />
            </div>

            <div>
              <Label htmlFor="admin-contact-emails">Notification Email Addresses</Label>
              <Textarea
                id="admin-contact-emails"
                placeholder="Enter email addresses separated by commas&#10;e.g., dor@company.com, areamanager@company.com"
                value={editingService?.contact_emails || ''}
                onChange={(e) => setEditingService({ ...editingService, contact_emails: e.target.value })}
                className="mt-1.5"
                rows={3}
                data-testid="admin-contact-emails-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                These addresses will be pre-filled when clicking "Send Outage Notification" on the dashboard
              </p>
            </div>

            <div>
              <Label htmlFor="admin-down-message">Down Message</Label>
              <Textarea
                id="admin-down-message"
                placeholder="Message to include in the notification email..."
                value={editingService?.down_message || ''}
                onChange={(e) => setEditingService({ ...editingService, down_message: e.target.value })}
                className="mt-1.5"
                rows={3}
                data-testid="admin-down-message-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                This message is shown on the dashboard and included in the outage email
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={isSaving} className="gap-2" data-testid="save-contacts-btn">
              <Save className="w-4 h-4" />
              {isSaving ? 'Saving...' : 'Save Contacts'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
